import { Injectable, inject } from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {map, Observable} from 'rxjs';
import { environment } from '../../../environments/environment';
import { CapitoliPort } from './capitoli.port';
import {
  CantiereAndamento,
  Capitolo, CreateCantiereAndamentoDto,
  CreateCapitoloDto, CreateExtraDto, CreateIncassoDto, Extra, Incasso,
  UpdateCantiereAndamentoDto,
  UpdateCapitoloDto, UpdateExtraDto, UpdateIncassoDto
} from '../../models/capitoli.model';
import {TenantService} from '../tenant.service';

@Injectable()
export class CapitoliHttpService extends CapitoliPort {
  private http = inject(HttpClient);
  private tenantService = inject(TenantService);
  private baseUrl = `${environment.apiBaseUrl || ''}/${this.tenantService.tenant}/capitoli`;
  private baseAndamentoURL = `${environment.apiBaseUrl || ''}/${this.tenantService.tenant}/andamenti`;
  private baseExtrasURL = `${environment.apiBaseUrl || ''}/${this.tenantService.tenant}/extras`;
  private baseIncassiURL = `${environment.apiBaseUrl || ''}/${this.tenantService.tenant}/incassi`;

  getById(id: number): Observable<Capitolo> {
    return this.http.get<Capitolo>(`${this.baseUrl}/${id}`);
  }

  getByPreventivoId(idPreventivo: number): Observable<Capitolo[]> {
    return this.http.get<Capitolo[]>(`${this.baseUrl}/getByPreventivo/${idPreventivo}`);
  }

  create(dto: CreateCapitoloDto): Observable<number> {
    return this.http.post<number>(this.baseUrl, dto);
  }

  update(id: number, dto: UpdateCapitoloDto, updateVoci: boolean = true): Observable<number> {
    return this.http.patch<number>(`${this.baseUrl}/${id}/${updateVoci}`, dto);
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

   getCantiereAndamentiByCantiereId(idCantiere: number): Observable<CantiereAndamento[]> {
    return this.http.get<any>(`${this.baseAndamentoURL}/byIdCantiere/${idCantiere}`, {
    }).pipe(
      map((res) => {
        if (Array.isArray(res?.content)) return res.content;
        if (Array.isArray(res?.data)) return res.data;
        if (Array.isArray(res)) return res;
        return [];
      })
    );
  }

   createCantiereAndamento(dto: CreateCantiereAndamentoDto): Observable<any> {
    return this.http.post<any>(`${this.baseAndamentoURL}`, dto);
  }

   updateCantiereAndamento(
    id: number,
    dto: UpdateCantiereAndamentoDto
  ): Observable<any> {
    return this.http.patch<any>(`${this.baseAndamentoURL}/${id}`, dto);
  }

   deleteCantiereAndamento(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseAndamentoURL}/${id}`);
  }

  getExtrasByCantiereId(idCantiere: number): Observable<Extra[]> {
    return this.http.get<any>(`${this.baseExtrasURL}/byIdCantiere/${idCantiere}`, {
    }).pipe(
      map((res) => {
        if (Array.isArray(res?.content)) return res.content;
        if (Array.isArray(res?.data)) return res.data;
        if (Array.isArray(res)) return res;
        return [];
      })
    );
  }

  createExtra(dto: CreateExtraDto): Observable<number> {
    return this.http.post<number>(this.baseExtrasURL, dto);
  }

  updateExtra(id: number, changes: UpdateExtraDto): Observable<number> {
    return this.http.patch<number>(`${this.baseExtrasURL}/${id}`, changes);
  }

  deleteExtra(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseExtrasURL}/${id}`);
  }

  getIncassiByCantiereId(idCantiere: number): Observable<Incasso[]> {
    return this.http.get<any>(`${this.baseIncassiURL}/byIdCantiere/${idCantiere}`, {
    }).pipe(
      map((res) => {
        if (Array.isArray(res?.content)) return res.content;
        if (Array.isArray(res?.data)) return res.data;
        if (Array.isArray(res)) return res;
        return [];
      })
    );
  }

  createIncasso(dto: CreateIncassoDto): Observable<number> {
    return this.http.post<number>(this.baseIncassiURL, dto);
  }

  updateIncasso(id: number, changes: UpdateIncassoDto): Observable<number> {
    return this.http.patch<number>(`${this.baseIncassiURL}/${id}`, changes);
  }

  deleteIncasso(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseIncassiURL}/${id}`);
  }

  setExtraStato(
    id: number,
    stato: string,
    createIncasso: boolean
  ): Observable<void> {
    const params = new HttpParams()
      .set('stato', stato)
      .set('createIncasso', String(createIncasso));

    return this.http.patch<void>(
      `${this.baseExtrasURL}/${id}/setStato`,
      null,
      { params }
    );
  }
}
